import { FC, useEffect } from 'react'
import { Provider, useSetAtom } from 'jotai'

import {
  Center,
  Flex,
  Spinner,
  StandardLayout
} from '@sequencehq/core-components'
import { ProtectedRoute, usePermissions } from 'lib/rbac'
import { ErrorMessage } from 'components/ErrorPages/ErrorMessage'
import { Canvas, Header, Sidebar, UnsavedChangesBlocker } from './components'
import { hasWritePermissionAtom } from './atoms'
import { useAutomationId, useLoadAutomationEditor } from './hooks'

// Mirror the user's RBAC write permission into the editor's jotai store so the
// canvas can render read-only
const useSyncWritePermission = () => {
  const { can, isLoading: isPermissionsLoading } = usePermissions()
  const setHasWritePermission = useSetAtom(hasWritePermissionAtom)

  const hasWrite = can('org.watchtower:create') || can('org.watchtower:update')

  useEffect(() => {
    if (isPermissionsLoading) return
    setHasWritePermission(hasWrite)
  }, [isPermissionsLoading, hasWrite, setHasWritePermission])
}

const AutomationEditorComponent: FC = () => {
  useSyncWritePermission()

  const { isLoading, isError, isNotFound } = useLoadAutomationEditor()

  if (isNotFound) {
    return <ErrorMessage errorType="notFound" />
  }

  if (isError) {
    return <ErrorMessage errorType="general" />
  }

  if (isLoading) {
    return (
      <Center boxSize="100%" data-testid="automations.editor.loading">
        <Spinner />
      </Center>
    )
  }

  return (
    <Flex width="100%" height="100%" overflow="hidden">
      {/**
       * The version-history sidebar is passed as StandardLayout's inspector so
       * it renders *inside* the floating panel rather than as a separate
       * surface flush against the viewport edge. `inspectorFullHeight` makes it
       * a full-height column (spanning the header row too), divided from the
       * header + canvas by the sidebar's own left border.
       */}
      <StandardLayout
        header={<Header />}
        content={<Canvas />}
        inspector={<Sidebar />}
        inspectorFullHeight
      />
      <UnsavedChangesBlocker />
    </Flex>
  )
}

export const AutomationEditor: FC = () => {
  const { isNewAutomation } = useAutomationId()
  const permission = isNewAutomation
    ? 'org.watchtower:create'
    : 'org.watchtower:read'

  return (
    <ProtectedRoute permission={permission}>
      <Provider>
        <AutomationEditorComponent />
      </Provider>
    </ProtectedRoute>
  )
}
